import { FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';

import { FormattedMessage } from 'dibs-react-intl';
import { FormattedPrice } from 'dibs-buyer-price';
import { Link } from 'dibs-elements/exports/Link';

import styles from './PdpSharedDetail.scss';
import { useItemTranslations } from '../PdpSharedOriginalLanguageProvider/PdpSharedOriginalLanguageProvider';

import { PdpSharedDetail_detailValues$key } from './__generated__/PdpSharedDetail_detailValues.graphql';

type OnClickMap = {
    [key: string]: {
        clickFn: () => void;
        dataTn: string;
    };
};

export type PdpSharedDetailProps = {
    detailValues: PdpSharedDetail_detailValues$key | null;
    currency: string;
    delimiter?: string | null;
    displayValuesAs?: 'block' | 'inline' | 'inline-block';
    // only optional b/c the `PdpSharedDetailCondition` component doesn't require this for some reason
    // otherwise every other pdp detail component does
    selectedSkuId?: string | null;
    onClickMap?: OnClickMap;
};

export const PdpSharedDetail: FC<PdpSharedDetailProps> = ({
    detailValues: detailValuesRef,
    delimiter,
    displayValuesAs,
    currency,
    selectedSkuId,
    onClickMap,
}) => {
    const detailValues =
        useFragment(
            graphql`
                fragment PdpSharedDetail_detailValues on PdpDetailValueInterface
                @relay(plural: true) {
                    detailKey
                    browseUrl
                    displayName
                    displayDetails
                    skuId
                    ... on PdpDetailVersionsValue {
                        price {
                            convertedAmountList {
                                currency
                                amount
                            }
                        }
                    }
                }
            `,
            detailValuesRef
        ) || [];
    const [interfaceState] = useItemTranslations();

    return (
        <>
            {detailValues.map((detailValue, index) => {
                if (selectedSkuId && detailValue.skuId && detailValue.skuId !== selectedSkuId) {
                    return null;
                }
                let { displayName } = detailValue;
                const { browseUrl, detailKey, displayDetails } = detailValue;
                if (
                    detailKey === 'conditionDetails' &&
                    interfaceState?.interfaceState === 'SHOW_ORIGINAL'
                ) {
                    displayName =
                        interfaceState?.data?.viewer.item?.pdpDetailsDisplay
                            ?.find(details => details?.detailKey === 'CONDITION')
                            ?.detailValues?.find(detail => detail?.detailKey === detailKey)
                            ?.displayName || displayName;
                }
                const key = `${detailKey}-${index}`;
                const nextIndex = index + 1;

                const isLastDetail = !detailValues?.[nextIndex];
                const addDelimiter = !isLastDetail && delimiter;

                if (!displayValuesAs) {
                    if (!delimiter && detailValues.length > 1) {
                        displayValuesAs = 'block';
                    } else if (addDelimiter) {
                        displayValuesAs = 'inline';
                    }
                }

                const detailClass = classnames({
                    [styles.detailBlock]: displayValuesAs === 'block',
                    [styles.detailInline]: displayValuesAs === 'inline',
                    [styles.detailInlineBlock]: displayValuesAs === 'inline-block',
                    [styles.inlineSpace]: !isLastDetail && displayValuesAs !== 'block',
                });

                let detailEl;
                const convertedAmts = detailValue?.price?.convertedAmountList;
                let priceEl;
                const convertedAmt =
                    convertedAmts && convertedAmts.find(amt => amt?.currency === currency);
                if (convertedAmt && convertedAmt.amount) {
                    priceEl = (
                        <span className={styles.secondaryLink}>
                            <FormattedMessage
                                id="abf.PdpSharedDetail.price"
                                defaultMessage="Price:"
                            />{' '}
                            <FormattedPrice
                                currency={currency}
                                value={Math.round(convertedAmt.amount)}
                            />
                        </span>
                    );
                }
                if (browseUrl) {
                    detailEl = (
                        <Link href={browseUrl} className={styles.details}>
                            {displayName}
                        </Link>
                    );
                } else if (detailKey && onClickMap?.[detailKey]) {
                    detailEl = (
                        <Link
                            onClick={onClickMap[detailKey].clickFn}
                            dataTn={onClickMap[detailKey].dataTn}
                            className={styles.details}
                        >
                            {displayName}
                            {priceEl}
                        </Link>
                    );
                } else {
                    detailEl = (
                        <span className={styles.details}>
                            {displayName}
                            {priceEl}
                        </span>
                    );
                }
                return (
                    <span
                        key={key}
                        className={detailClass}
                        data-tn={`pdp-spec-detail-${detailKey}`}
                    >
                        {detailEl}
                        {displayDetails && <> {displayDetails}</>}
                        {addDelimiter && delimiter}
                    </span>
                );
            })}
        </>
    );
};
